/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { LocalizationWithComponent } from '@/components/blocks/LocalizationWithComponent';
import { PasswordInput } from '@/components/blocks/PasswordInput';
import { RegisterOrCheckout } from '@/components/content/Login/parts/RegisterOrCheckout';
import {
	LoginDrawerSx,
	loginInputDrawerSx,
	loginInputSx,
	LoginSx,
} from '@/components/content/Login/styles/container';
import { validateGuestEmail } from '@/data/Content/GuestEmailValidate';
import { UserLogon } from '@/data/Content/Login';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { ADDRESS_FIELD_LENGTH } from '@/data/constants/addressFields';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REGEX } from '@/data/constants/regex';
import { useRecurringOrderState } from '@/data/state/useRecurringOrderState';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { logEvent } from '@/data/utils/logEventsUtils';
import { REG_EX } from '@/utils/address';
import { mergeSx } from '@/utils/mergeSx';
import { useForm } from '@/utils/useForm';
import { useMobileApp } from '@/utils/useMobileApp';
import {
	Alert,
	Box,
	Button,
	Divider,
	FormControlLabel,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { GuestEmailAvailabilityRequest } from 'integration/generated/custom/data-contracts';
import { saveFacebookCAPIUserAttributes } from 'presentation/events/utils/facebookUtils';
import { ChangeEvent, FC, SetStateAction, useEffect, useState } from 'react';
import { CHECKOUT } from 'shared/custom/constants/commonConstants';

const initLoginValues: UserLogon = {
	logonId: '',
	logonPassword: '',
	rememberMe: false,
	resendToken: false,
	verificationToken: '',
	isSessionExpired: '',
};

type Props = {
	onSubmit: (value: UserLogon) => Promise<boolean | void>;
	isDialogView?: boolean;
	drawerVersion?: boolean;
	closeDrawer?: () => void;
	onGoToForgotPassword?: () => void;
};

// eslint-disable-next-line complexity
export const LoginForm: FC<Props> = ({
	onSubmit,
	isDialogView = false,
	drawerVersion = false,
	closeDrawer,
	onGoToForgotPassword,
}) => {
	const RouteLocal = useLocalization('Routes');
	const signInNLS = useLocalization('SignIn');
	const registerLabels = useLocalization('RegistrationLayout');
	const [recurringMessage, setRecurringMessage] = useState<string>('');
	const [isLoginError, setIsLoginError] = useState<boolean>(false);
	const [guestCheckout, setGuestCheckout] = useState<boolean>(false);
	const [emailGuest, setEmailGuest] = useState<string>(EMPTY_STRING);

	const { handleInputChange, values: loginValues, formRef, submitting } = useForm(initLoginValues);

	const router = useNextRouter();
	const { settings } = useSettings();
	const { isMobileApp } = useMobileApp();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const {
		recurringOrderInfo: { isRecurring },
	} = useRecurringOrderState();
	const { user, mutateUser } = useUser();

	const checkoutFlow = router.query.flow === CHECKOUT;
	const signInTitle = checkoutFlow
		? signInNLS.SignInAndCheckoutTitle.t()
		: signInNLS.SignInTitle.t();
	const signInButton = checkoutFlow
		? signInNLS.SignInAndCheckoutButton.t()
		: signInNLS.SignInButton.t();
	const recurringMessageToShow = signInNLS.Msgs.RecurringOrder.t();

	useEffect(() => {
		// recurring order state only at client, use useEffect to avoid hydration error
		if (checkoutFlow && isRecurring) {
			setRecurringMessage(recurringMessageToShow);
		}
	}, [checkoutFlow, isRecurring, recurringMessageToShow]);

	const [logonIdErrorMsg, setLogonIdErrorMsg] = useState(EMPTY_STRING);
	const [guestErrorMsg, setGuestErrorMsg] = useState(EMPTY_STRING);
	const [logonIdError, setLogonIdError] = useState(false);
	const [guestError, setGuestError] = useState(false);
	const [passwordError, setPasswordError] = useState(false);

	const isLoginIdError = (
		email: string,
		setError: (value: SetStateAction<boolean>) => void,
		setErrorMsg: (value: SetStateAction<string>) => void
	) => {
		const invalidFormat = !REGEX.EMAIL.test(email);

		if (invalidFormat) {
			setError(true);
			setErrorMsg(registerLabels.Msgs.InvalidFormat.t());
			return true;
		}

		setError(false);
		setErrorMsg(EMPTY_STRING);
		return false;
	};

	const canContinue = (email: string, psw?: string) => {
		let flag = true;
		const pswExist = typeof psw === 'undefined' ? true : psw !== EMPTY_STRING;

		if (email.trim() !== EMPTY_STRING && pswExist) {
			flag = !REGEX.EMAIL.test(email);
		}

		return flag;
	};

	const isPasswordError = () => {
		const { logonPassword } = loginValues;
		if (logonPassword === '') {
			setPasswordError(true);
			return true;
		}
		return false;
	};

	const tryOnSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validLogin = isLoginIdError(
			loginValues.logonId || '',
			setLogonIdError,
			setLogonIdErrorMsg
		);
		const validPass = isPasswordError();
		if (validLogin || validPass) return;
		setLogonIdErrorMsg('');
		const isLoginSuccess = await onSubmit(loginValues);
		setIsLoginError(!isLoginSuccess);
		setPasswordError(false);
	};

	const handleForgotPassword = () => {
		closeDrawer?.();
		if (isMobileApp) {
			logEvent('forgotPassword');
			return;
		}

		if (onGoToForgotPassword) {
			onGoToForgotPassword();
			return;
		}

		router.push(`/${RouteLocal.ForgotPassword.route.t()}`);
	};

	const forgotPassword = (
		<Typography
			variant="bodyBaseline"
			sx={mergeSx(
				LoginSx.forgotPasswordText,
				drawerVersion ? LoginDrawerSx.forgotPasswordText : {}
			)}
		>
			<Typography
				component="span"
				variant="bodyBaseline"
				onClick={handleForgotPassword}
				data-testid={RouteLocal.ForgotPassword.route.t()}
				id={RouteLocal.ForgotPassword.route.t()}
				sx={mergeSx(LoginSx.inlineLink, drawerVersion ? LoginDrawerSx.inlineLink : {}, {
					cursor: 'pointer',
				})}
			>
				{signInNLS.ForgotPassword.t()}
			</Typography>
		</Typography>
	);

	const showEmailInput = () => {
		setGuestCheckout(true);
	};

	const continueGuest = async () => {
		const normalizedEmailGuest = emailGuest.toLowerCase();
		const data: GuestEmailAvailabilityRequest = {
			emailId: normalizedEmailGuest,
			nickname: normalizedEmailGuest,
		};
		const emailAvailability = await validateGuestEmail(storeId, data, params);
		if (!emailAvailability) {
			setGuestError(true);
			setGuestErrorMsg(signInNLS.ExistingEmail.t());
		} else {
			mutateUser(
				{
					...user,
					email: normalizedEmailGuest,
					firstName: EMPTY_STRING,
					lastName: EMPTY_STRING,
					phone1: EMPTY_STRING,
				},
				{ revalidate: false }
			);
			saveFacebookCAPIUserAttributes({
				email: normalizedEmailGuest,
				phone: null,
				isGuest: true,
			});
			router.push(RouteLocal.CheckOut.route.t());
		}
	};

	const handleGuestEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.trim();
		setEmailGuest(value);
		if (value === EMPTY_STRING) {
			setGuestError(false);
			setGuestErrorMsg(EMPTY_STRING);
		} else {
			isLoginIdError(value, setGuestError, setGuestErrorMsg);
		}
	};

	return (
		<Stack component="form" onSubmit={(event) => tryOnSubmit(event)} noValidate ref={formRef}>
			{!isDialogView || drawerVersion ? (
				<Box
					display="flex"
					sx={mergeSx(LoginSx.titleContainer, drawerVersion ? LoginDrawerSx.titleContainer : {})}
				>
					<Typography
						variant="headingSegment"
						sx={mergeSx(LoginSx.title, drawerVersion ? LoginDrawerSx.title : {})}
					>
						{signInTitle}
					</Typography>
					<Divider
						sx={mergeSx(LoginSx.titleDivider, drawerVersion ? LoginDrawerSx.titleDivider : {})}
					/>
				</Box>
			) : null}
			{recurringMessage ? (
				<Alert variant="standard" severity="warning">
					{recurringMessage}
				</Alert>
			) : null}
			<TextField
				variant="outlined"
				margin="normal"
				name="logonId"
				autoComplete="username"
				label={signInNLS.Label.Email.t()}
				autoFocus
				value={loginValues.logonId}
				onChange={(event: ChangeEvent<HTMLInputElement>) => {
					setLogonIdErrorMsg('');
					handleInputChange(event);
				}}
				inputProps={{
					maxLength: ADDRESS_FIELD_LENGTH.logonId,
					type: 'email',
					placeholder: signInNLS.emailPlaceholder.t(),
					pattern: REG_EX.EMAIL.source,
					required: true,
				}}
				error={logonIdError}
				helperText={logonIdErrorMsg}
				placeholder={registerLabels.emailPlaceholder.t()}
				sx={mergeSx(loginInputSx, drawerVersion ? loginInputDrawerSx : {})}
				FormHelperTextProps={{ sx: LoginSx.helperText }}
				onBlur={() => {
					if (loginValues.logonId) {
						isLoginIdError(loginValues.logonId, setLogonIdError, setLogonIdErrorMsg);
						return;
					}
					setLogonIdError(false);
					setLogonIdErrorMsg(EMPTY_STRING);
				}}
			/>
			<PasswordInput
				name="logonPassword"
				label={signInNLS.Password.t()}
				value={loginValues.logonPassword}
				onChange={(event: ChangeEvent<HTMLInputElement>) => {
					setPasswordError(false);
					handleInputChange(event);
				}}
				error={passwordError || isLoginError}
				helperText={passwordError || isLoginError ? signInNLS.Msgs.InvalidCredentials.t() : ''}
				inputProps={{
					maxLength: 100,
					required: true,
				}}
				sx={mergeSx(LoginSx.passwordInput, drawerVersion ? loginInputDrawerSx : {})}
			/>
			{drawerVersion ? (
				forgotPassword
			) : (
				<FormControlLabel
					control={
						<Switch
							name="rememberMe"
							checked={loginValues.rememberMe}
							onChange={handleInputChange}
							color="primary"
						/>
					}
					label={signInNLS.Label.rememberMe.t()}
					sx={LoginSx.switchLabel}
				/>
			)}
			<Stack>
				<Button
					type="submit"
					variant="contained"
					sx={LoginSx.button}
					disabled={
						drawerVersion
							? canContinue(`${loginValues.logonId ?? ''}`, loginValues.logonPassword)
							: submitting
					}
					data-testid="button-sign-in-submit"
					id={drawerVersion ? 'cartSignInButton' : 'button-sign-in-submit'}
					fullWidth
				>
					{drawerVersion ? signInNLS.Continue.t() : signInButton}
				</Button>
				{drawerVersion ? null : forgotPassword}
				<RegisterOrCheckout drawerVersion={drawerVersion} closeDrawer={closeDrawer} />
				<Divider sx={mergeSx(LoginSx.divider, drawerVersion ? LoginDrawerSx.divider : {})} />
				{drawerVersion ? (
					<>
						<Typography variant="bodySubText">
							<LocalizationWithComponent
								text={signInNLS.ContinueAsGuest.t()}
								components={[
									<Typography
										key="0"
										sx={mergeSx(LoginSx.inlineLink, LoginDrawerSx.inlineLink)}
										onClick={() => showEmailInput()}
										variant="bodySubText"
										id="cartGuestLink"
									/>,
								]}
							/>
						</Typography>
						{guestCheckout ? (
							<Stack spacing={'10px'} mt={'10px'}>
								<TextField
									variant="outlined"
									margin="normal"
									name="emailGuest"
									autoComplete="username"
									label={signInNLS.Label.Email.t()}
									value={emailGuest}
									onChange={handleGuestEmailChange}
									inputProps={{
										maxLength: ADDRESS_FIELD_LENGTH.logonId,
										type: 'email',
										placeholder: signInNLS.emailPlaceholder.t(),
										pattern: REG_EX.EMAIL.source,
									}}
									error={guestError}
									helperText={guestErrorMsg}
									placeholder={registerLabels.emailPlaceholder.t()}
									sx={mergeSx(loginInputSx, loginInputDrawerSx)}
									FormHelperTextProps={{ sx: LoginSx.helperText }}
									onBlur={() => {
										if (emailGuest !== '') {
											isLoginIdError(emailGuest, setGuestError, setGuestErrorMsg);
											return;
										}
										setGuestError(false);
										setGuestErrorMsg(EMPTY_STRING);
									}}
								/>
								<Button
									type="button"
									variant="contained"
									sx={LoginSx.button}
									disabled={canContinue(emailGuest)}
									onClick={continueGuest}
									fullWidth
									id="cartGuestButton"
								>
									{signInNLS.Continue.t()}
								</Button>
							</Stack>
						) : null}
					</>
				) : (
					<Typography variant="bodySubText" sx={LoginSx.text}>
						{signInNLS.ProtectedSite.t()}{' '}
						<Linkable
							href={signInNLS.GooglePrivacyURL.t()}
							type="link"
							id="login-form-google-privacy-link"
							data-testid="login-form-google-privacy-link"
							sx={LoginSx.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							{signInNLS.PrivacyPolicy.t()}
						</Linkable>{' '}
						{registerLabels.And.t()}{' '}
						<Linkable
							href={signInNLS.GoogleTermsURL.t()}
							type="link"
							id="login-form-google-terms-link"
							data-testid="login-form-google-terms-link"
							sx={LoginSx.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							{signInNLS.ServiceTerms.t()}
						</Linkable>{' '}
						{signInNLS.OfGoogle.t()}
					</Typography>
				)}
			</Stack>
		</Stack>
	);
};
